import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const RecentTransactions = () => {
    const { companyId } = useParams();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecentTransactions();
    }, [companyId]);

    const fetchRecentTransactions = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axiosInstance.get(
                getApiUrl(`${companyId}/dashboard/recent-transactions`),
                {
                    params: { limit: 6 }, // Get 8 recent transactions
                    withCredentials: true
                }
            );

            setTransactions(response.data);
        } catch (error) {
            console.error('Error fetching recent transactions:', error);
            setError('Kunde inte ladda transaktioner');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.abs(amount));
    };

    const getTransactionIcon = (type, category) => {
        if (type === 'income') {
            return (
                <div style={{
                    width: '48px',
                    height: '48px',
                    minWidth: '48px', // Prevent shrinking
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'white' }}>
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            );
        } else {
            // Different icons for different expense categories
            let iconColor = '#ef4444';
            let bgGradient = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';

            if (category === 'Personal' || category === 'Löner') {
                iconColor = '#f59e0b';
                bgGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
            } else if (category === 'Lokalkostnader') {
                iconColor = '#8b5cf6';
                bgGradient = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
            } else if (category === 'Material') {
                iconColor = '#06b6d4';
                bgGradient = 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';
            }

            return (
                <div style={{
                    width: '48px',
                    height: '48px',
                    minWidth: '48px', // Prevent shrinking
                    borderRadius: '12px',
                    background: bgGradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${iconColor}30`
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'white' }}>
                        <path d="M19 7L5 21L12 14L19 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M5 7L19 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            );
        }
    };

    const getCategoryBadge = (type, category) => {
        const badgeStyles = {
            income: {
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                color: '#166534',
                border: '1px solid #bbf7d020'
            },
            expense: {
                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                color: '#dc2626',
                border: '1px solid #fecaca20'
            }
        };

        // Customize badge colors for specific categories
        if (category === 'Personal' || category === 'Löner') {
            badgeStyles.expense = {
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                color: '#d97706',
                border: '1px solid #fde68a20'
            };
        } else if (category === 'Lokalkostnader') {
            badgeStyles.expense = {
                background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                color: '#7c3aed',
                border: '1px solid #ddd6fe20'
            };
        } else if (category === 'Material') {
            badgeStyles.expense = {
                background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)',
                color: '#0891b2',
                border: '1px solid #a5f3fc20'
            };
        }

        return (
            <span style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                padding: '0.25rem 0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap', // Prevent badge text wrapping
                ...badgeStyles[type]
            }}>
                {category}
            </span>
        );
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #e2e8f0',
                        borderTop: '3px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        Laddar transaktioner...
                    </span>
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                borderRadius: '16px',
                border: '1px solid #fecaca'
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                        <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </div>
                <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    {error}
                </p>
                <button
                    onClick={fetchRecentTransactions}
                    style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #dc2626',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                    }}
                >
                    Försök igen
                </button>
            </div>
        );
    }

    return (
        <div>
            {transactions.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem 1rem',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-400">
                            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </div>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                        Inga transaktioner att visa
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {transactions.map((transaction) => (
                        <div
                            key={transaction.id}
                            style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '1.25rem',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '1rem' // Add gap between left and right sections
                            }}>
                                {/* Left section with icon and content */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: '1', // Take available space
                                    minWidth: '0', // Allow text truncation
                                    gap: '0.75rem'
                                }}>
                                    {getTransactionIcon(transaction.type, transaction.category)}
                                    <div style={{
                                        flex: '1',
                                        minWidth: '0', // Allow text truncation
                                        overflow: 'hidden'
                                    }}>
                                        <h6 style={{
                                            margin: '0 0 0.5rem 0',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1e293b',
                                            lineHeight: '1.4',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap' // Truncate long descriptions
                                        }}
                                            title={transaction.description} // Show full text on hover
                                        >
                                            {transaction.description}
                                        </h6>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: '#64748b',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            flexWrap: 'wrap' // Allow wrapping on small screens
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                    <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
                                                </svg>
                                                {moment(transaction.date).format('D MMM YYYY')}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                    <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {transaction.verification}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right section with amount and badge */}
                                <div style={{
                                    textAlign: 'right',
                                    flexShrink: '0', // Don't shrink
                                    minWidth: 'fit-content'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '700',
                                        color: transaction.type === 'income' ? '#10b981' : '#ef4444',
                                        marginBottom: '0.5rem',
                                        letterSpacing: '-0.025em',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {transaction.type === 'income' ? '+' : ''}{formatCurrency(transaction.amount)}
                                    </div>
                                    {getCategoryBadge(transaction.type, transaction.category)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RecentTransactions;
